import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { NumberInput } from '../../components/NumberInput';
import { SearchableCombobox } from '../../components/ui/searchable-combobox';
import { toast } from 'sonner';
import {
  Search, Plus, Trash2, Send, Loader2, FileText, ChevronRight,
  ChevronLeft, Building, Package, CheckCircle, X, Users, FileSearch,
  ClipboardList, Edit2, Lock
} from 'lucide-react';

const STEPS = ['Items a Cotizar', 'Proveedores', 'Revisar y Enviar'];

/**
 * Iter 292.b (Carlos, feb-2026) — Input con confirmación al perder foco.
 * El usuario puede escribir libremente sin ser interrumpido; al hacer blur
 * (o Enter) dispara window.confirm con el mensaje configurado.
 *   - Si acepta → onCommit(nuevoValor).
 *   - Si cancela o no hubo cambio → vuelve al valor original.
 */
function EditableInput({ value, onCommit, confirmMessage, className = '', type = 'text', step, min, placeholder, testid }) {
  const [draft, setDraft] = useState(value ?? '');
  useEffect(() => { setDraft(value ?? ''); }, [value]);
  const commit = () => {
    const nueva = String(draft ?? '').trim();
    const actual = String(value ?? '').trim();
    if (nueva === actual) return;
    if (!window.confirm(confirmMessage)) {
      setDraft(actual); // revertir
      return;
    }
    onCommit(draft);
  };
  return (
    <Input
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); e.target.blur(); } }}
      type={type}
      step={step}
      min={min}
      placeholder={placeholder}
      className={className}
      data-testid={testid}
    />
  );
}

export default function SolicitudCotizacion() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const [step, setStep] = useState(0);

  // Step 1: Items
  const [items, setItems] = useState([]);
  const [manualDesc, setManualDesc] = useState('');
  const [manualCant, setManualCant] = useState('');
  const [manualUnd, setManualUnd] = useState('UN');
  const [matSearch, setMatSearch] = useState('');
  const [matResults, setMatResults] = useState([]);
  const [matLoading, setMatLoading] = useState(false);
  const [showMatResults, setShowMatResults] = useState(false);
  const [manualMode, setManualMode] = useState(false);

  // Step 2: Providers
  const [proveedores, setProveedores] = useState([]);
  const [provSearch, setProvSearch] = useState('');
  const [provResults, setProvResults] = useState([]);
  const [searchingProv, setSearchingProv] = useState(false);

  // Step 3: Review
  const [diasVenc, setDiasVenc] = useState(5);
  const [mensajeAdicional, setMensajeAdicional] = useState('');
  // Iter 286.n — OT a nivel solicitud (dropdown "Por definir" o número OT vigente)
  const [otSolicitud, setOtSolicitud] = useState(''); // '' = sin elegir, 'por_definir' = Por definir, o número
  const [otsActivas, setOtsActivas] = useState([]);
  const [otsLoading, setOtsLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [sendingText, setSendingText] = useState('');
  const [sendError, setSendError] = useState('');
  const [pdfPreviewUrl, setPdfPreviewUrl] = useState(null);
  const [, setCreatedId] = useState(null);
  const [sendResult, setSendResult] = useState(null);

  // Iter 292.b (Carlos, feb-2026) — Cargar Requisiciones "pendiente" o "en_proceso"
  // El usuario elige una o más requisiciones y sus ítems se agregan al detalle
  // a cotizar (con el número de requisición en cada ítem para trazabilidad).
  const [requisicionesDisponibles, setRequisicionesDisponibles] = useState([]);
  const [requisicionesLoading, setRequisicionesLoading] = useState(false);
  const [requisicionesCargadas, setRequisicionesCargadas] = useState([]); // [{numero, ot_numero, ot_descripcion, creado_por, items_count}]
  const [reqComboValue, setReqComboValue] = useState('');

  // rol admin/gerencia puede sobrescribir la descripción del producto
  const isAdminOrGerencia = user && (
    user.rol === 'admin' || user.rol === 'administrador' || user.rol === 'gerencia'
  );

  // Iter 286.n / Iter 292.n (Carlos, feb-2026) — cargar OTs vigentes al
  // montar la página. Fix: usar solo_abiertas=true (excluye COMPLETADAS,
  // consistente con Generar OC) y subir limit a 500 para que OTs de gastos
  // administrativos con número bajo (997, 500, 100, etc.) también aparezcan.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setOtsLoading(true);
      try {
        const res = await api.get('/compras/ots-activas?solo_abiertas=true&limit=500');
        if (!cancelled) setOtsActivas(res.data?.ots || []);
      } catch { if (!cancelled) setOtsActivas([]); }
      setOtsLoading(false);
    })();
    return () => { cancelled = true; };
  }, []);

  // Iter 292.b — cargar Requisiciones autorizadas al montar
  // Iter 292.j (Carlos, feb-2026, correction): SOLO Requisiciones AUTORIZADAS
  // (estado=aprobada). Antes cargaba pendiente + en_proceso.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setRequisicionesLoading(true);
      try {
        const res = await api.get('/compras/requisiciones?estado=aprobada&limit=200');
        if (!cancelled) setRequisicionesDisponibles(res.data?.requisiciones || []);
      } catch { if (!cancelled) setRequisicionesDisponibles([]); }
      setRequisicionesLoading(false);
    })();
    return () => { cancelled = true; };
  }, []);

  // Iter 292.b — agregar una requisición: sus items se pegan en el detalle a cotizar
  const cargarRequisicion = async (numeroReq) => {
    const req = requisicionesDisponibles.find(r => String(r.numero) === String(numeroReq));
    if (!req) {
      toast.error('Requisición no encontrada');
      return;
    }
    if (requisicionesCargadas.some(r => String(r.numero) === String(req.numero))) {
      toast.warning(`Requisición ${req.numero} ya fue cargada`);
      return;
    }
    // Buscar descripción de la OT (si tiene) desde el listado de otsActivas
    let otDescripcion = '';
    if (req.ot_numero) {
      const ot = otsActivas.find(o => String(o.numero_ot) === String(req.ot_numero));
      otDescripcion = ot?.descripcion || '';
    }
    const nuevosItems = (req.items || []).map(it => ({
      descripcion: it.descripcion || '',
      cantidad: Number(it.cantidad) || 0,
      unidad: it.unidad || 'UN',
      codigo: it.codigo_material || '',
      observacion: it.observaciones || '',
      ot_numero: it.ot_numero ? String(it.ot_numero) : (req.ot_numero ? String(req.ot_numero) : ''),
      requisicion: String(req.numero),
      requisicion_creado_por: req.creado_por || '',
      familia: '',
    }));
    setItems(prev => [...prev, ...nuevosItems]);
    setRequisicionesCargadas(prev => [...prev, {
      numero: req.numero,
      ot_numero: req.ot_numero || '',
      ot_descripcion: otDescripcion,
      creado_por: req.creado_por || '',
      items_count: nuevosItems.length,
    }]);
    setReqComboValue('');
    toast.success(`Requisición ${req.numero}: ${nuevosItems.length} ítem(s) agregado(s)`);
  };

  // Iter 292.b — descargar una requisición ya cargada (remueve sus ítems)
  const descargarRequisicion = (numeroReq) => {
    const req = requisicionesCargadas.find(r => String(r.numero) === String(numeroReq));
    if (!req) return;
    if (!window.confirm(`¿Está seguro de quitar la Requisición ${numeroReq} y sus ${req.items_count} ítem(s) del detalle?`)) return;
    setItems(prev => prev.filter(it => String(it.requisicion) !== String(numeroReq)));
    setRequisicionesCargadas(prev => prev.filter(r => String(r.numero) !== String(numeroReq)));
    toast.success(`Requisición ${numeroReq} desasociada`);
  };

  // Load pending items from query params if provided
  useEffect(() => {
    const pendingItems = searchParams.get('items');
    if (pendingItems) {
      try {
        const parsed = JSON.parse(decodeURIComponent(pendingItems));
        if (Array.isArray(parsed) && parsed.length > 0) {
          setItems(parsed.map(it => ({
            descripcion: it.descripcion || '',
            cantidad: it.cantidad_pendiente || it.cantidad_solicitada || 0,
            unidad: it.unidad || 'UN',
            codigo: it.codigo || '',
            observacion: it.observacion || it.observaciones || '',
            ot_numero: it.ot_numero || '',
            requisicion: it.requisicion || '',
            familia: it.familia || it.familia_nombre || '',
            subfamilia_codpf: it.subfamilia_codpf || '',
          })));
        }
      } catch { }
    }
    // Load items from sugerencias (sessionStorage)
    const storedCotizarItems = sessionStorage.getItem('cotizar_items');
    if (storedCotizarItems) {
      try {
        const parsed = JSON.parse(storedCotizarItems);
        if (Array.isArray(parsed) && parsed.length > 0) {
          setItems(parsed.map(it => ({
            descripcion: it.descripcion || '',
            cantidad: it.cantidad || 0,
            unidad: it.unidad || 'UN',
            codigo: it.codigo || '',
            observacion: it.observacion || it.observaciones || '',
            ot_numero: it.ot_numero || '',
            requisicion: it.requisicion || '',
            familia: it.familia || '',
          })));
        }
        sessionStorage.removeItem('cotizar_items');
      } catch { }
    }
  }, [searchParams]);

  // Iter 286.t / Iter 292.o (Carlos, feb-2026) — Sincronizar la OT del
  // encabezado con las OTs de los ítems del detalle:
  //   • Si TODOS los ítems comparten UNA sola OT → auto-set en el encabezado
  //     (aparece en el PDF como "OT: N - Descripción", SIN Referencia Interna).
  //   • Si hay MÚLTIPLES OTs distintas → bloquear el combo y forzar
  //     "por_definir" (el PDF listará las OTs como Referencia Interna al pie).
  //   • Si no hay OTs en los ítems → dejar libre para elección manual.
  const uniqueOtsItems = useMemo(() => (
    Array.from(new Set(
      items.map(it => String(it.ot_numero || '').trim()).filter(Boolean)
    ))
  ), [items]);
  const otsBloqueadaMulti = uniqueOtsItems.length > 1;

  useEffect(() => {
    if (uniqueOtsItems.length === 1 && otSolicitud !== uniqueOtsItems[0]) {
      setOtSolicitud(uniqueOtsItems[0]);
    } else if (otsBloqueadaMulti && otSolicitud !== 'por_definir') {
      // Forzar "por_definir" cuando hay múltiples → el PDF listará las OTs
      // como Referencia Interna, no en el encabezado.
      setOtSolicitud('por_definir');
    }
  }, [uniqueOtsItems, otsBloqueadaMulti, otSolicitud]);

  // Material search
  const searchMaterials = useCallback(async (query) => {
    if (!query || query.length < 2) { setMatResults([]); return; }
    setMatLoading(true);
    try {
      const res = await api.get(`/compras/materiales?q=${encodeURIComponent(query)}&limit=750`);
      setMatResults(res.data?.materiales || []);
      setShowMatResults(true);
    } catch { setMatResults([]); }
    setMatLoading(false);
  }, []);

  useEffect(() => {
    if (manualMode) return;
    const t = setTimeout(() => searchMaterials(matSearch), 300);
    return () => clearTimeout(t);
  }, [matSearch, searchMaterials, manualMode]);

  const addMaterialItem = (mat) => {
    setItems(prev => [...prev, {
      descripcion: mat.descripcion2 || mat.descripcion || '',
      cantidad: 1,
      unidad: mat.unidad || 'UN',
      codigo: mat.codigo || '',
      observacion: '',
      ot_numero: '',
      requisicion: '',
      familia: mat.familia_nombre || mat.subfamilia_nombre || '',
    }]);
    setMatSearch('');
    setMatResults([]);
    setShowMatResults(false);
  };

  // Add manual item
  const addManualItem = () => {
    if (!manualDesc.trim()) { toast.error('Ingrese una descripcion'); return; }
    setItems(prev => [...prev, {
      descripcion: manualDesc.trim(),
      cantidad: parseFloat(manualCant) || 1,
      unidad: manualUnd || 'UN',
      codigo: '', observacion: '', ot_numero: '', requisicion: '', familia: '',
    }]);
    setManualDesc(''); setManualCant(''); setManualUnd('UN');
  };

  const removeItem = (idx) => {
    const item = items[idx];
    if (!item) return;
    const nombre = (item.descripcion || '').trim() || `Ítem ${idx + 1}`;
    if (!window.confirm(`¿Está seguro de eliminar el ítem: ${nombre}?`)) return;
    setItems(prev => prev.filter((_, i) => i !== idx));
  };
  const updateItemObs = (idx, obs) => {
    setItems(prev => prev.map((it, i) => i === idx ? { ...it, observacion: obs } : it));
  };
  // Iter 286.u — Cantidad y unidad editables inline por el usuario
  // Iter 292.b — la confirmación se dispara desde <EditableInput> al perder foco.
  const updateItemCantidad = (idx, cant) => {
    setItems(prev => prev.map((it, i) => i === idx ? { ...it, cantidad: cant } : it));
  };
  const updateItemUnidad = (idx, und) => {
    setItems(prev => prev.map((it, i) => i === idx ? { ...it, unidad: und } : it));
  };
  // Iter 292.b — Cambio de PRODUCTO (descripción): solo por el emisor de la
  // Requisición o roles Admin/Gerencia. Los ítems agregados manualmente son
  // siempre editables (no vienen de una requisición ajena).
  const canEditProducto = (item) => {
    if (!item) return false;
    if (!item.requisicion) return true; // ítem manual o del catálogo → editable
    if (isAdminOrGerencia) return true;
    if (user?.email && item.requisicion_creado_por && user.email === item.requisicion_creado_por) return true;
    return false;
  };
  const updateItemDescripcion = (idx) => {
    const item = items[idx];
    if (!item) return;
    if (!canEditProducto(item)) {
      toast.error('Solo el emisor de la Requisición, Gerencia o Administrador puede cambiar el producto');
      return;
    }
    const nueva = window.prompt('Cambiar producto — nueva descripción:', item.descripcion || '');
    if (nueva === null) return; // cancelado
    const limpia = nueva.trim();
    if (!limpia) { toast.error('La descripción no puede quedar vacía'); return; }
    setItems(prev => prev.map((it, i) => i === idx ? { ...it, descripcion: limpia } : it));
  };

  // Provider search
  const searchProveedores = useCallback(async (query) => {
    setSearchingProv(true);
    try {
      const res = await api.get(`/compras/cotizaciones/proveedores/buscar?q=${encodeURIComponent(query)}&limit=15`);
      setProvResults(res.data?.proveedores || []);
    } catch { setProvResults([]); }
    setSearchingProv(false);
  }, []);

  useEffect(() => {
    const t = setTimeout(() => { searchProveedores(provSearch); }, 300);
    return () => clearTimeout(t);
  }, [provSearch, searchProveedores]);

  const addProveedor = (prov) => {
    if (proveedores.some(p => p.email === prov.email && p.nombre === prov.nombre)) {
      toast.error('Proveedor ya agregado'); return;
    }
    setProveedores(prev => [...prev, { ...prov }]);
  };

  const removeProveedor = (idx) => setProveedores(prev => prev.filter((_, i) => i !== idx));
  const updateProvEmail = (idx, email) => {
    setProveedores(prev => prev.map((p, i) => i === idx ? { ...p, email } : p));
  };

  // Navigation
  const canGoNext = useMemo(() => {
    if (step === 0) {
      // Iter 286.u — Validar que todos los items tengan cantidad > 0
      return items.length > 0 && items.every(it => Number(it.cantidad) > 0);
    }
    if (step === 1) return proveedores.length > 0 && proveedores.every(p => p.email);
    return true;
  }, [step, items, proveedores]);

  const nextStep = () => { if (canGoNext && step < 2) setStep(step + 1); };
  const prevStep = () => { if (step > 0) setStep(step - 1); };

  // Create & Send
  const handleCrearYEnviar = async () => {
    if (items.length === 0 || proveedores.length === 0) return;
    setSending(true);
    setSendingText('Creando solicitud...');
    setSendError('');
    let scId = null;
    let scNum = '';

    try {
      // Step 1: Create
      const cleanProvs = proveedores.map(p => ({
        nombre: String(p.nombre || ''),
        rut: String(p.rut || ''),
        email: String(p.email || ''),
      }));
      const cleanItems = items.map(it => ({
        descripcion: String(it.descripcion || ''),
        cantidad: Number(it.cantidad) || 0,
        unidad: String(it.unidad || 'UN'),
        codigo: String(it.codigo || ''),
        observacion: String(it.observacion || ''),
        ot_numero: String(it.ot_numero || ''),
        requisicion: String(it.requisicion || ''),
        familia: String(it.familia || ''),
        subfamilia_codpf: String(it.subfamilia_codpf || ''),
      }));
      const createRes = await api.post('/compras/cotizaciones', {
        items: cleanItems,
        proveedores: cleanProvs,
        dias_vencimiento: Number(diasVenc) || 5,
        mensaje_adicional: mensajeAdicional || '',
        // Iter 286.n — OT a nivel solicitud
        ot_numero: otSolicitud || '',
      });
      scId = createRes.data?.id;
      scNum = createRes.data?.numero || '';
      if (!scId) throw new Error('No se recibio ID de solicitud');
      setCreatedId(scId);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const status = err?.response?.status;
      const msg = detail
        ? `Error ${status || ''}: ${typeof detail === 'string' ? detail : JSON.stringify(detail)}`
        : `Error al crear solicitud: ${err?.message || 'Sin respuesta del servidor'}`;
      setSendError(msg);
      toast.error(msg);

      setSending(false);
      setSendingText('');
      return;
    }

    // Creation succeeded — ALWAYS show success after this point
    let resultData = { enviados: 0, errores: [], total: proveedores.length };
    try {
      setSendingText(`Enviando correos a ${proveedores.length} proveedor(es)...`);
      const sendRes = await api.post(`/compras/cotizaciones/${scId}/enviar`);
      resultData.enviados = sendRes.data?.enviados || 0;
      resultData.errores = Array.isArray(sendRes.data?.errores) ? sendRes.data.errores : [];
    } catch (err) {
      resultData.errores = [err?.response?.data?.detail || 'Error al enviar correos'];

    }

    try {
      setSendResult(resultData);
      if (resultData.enviados > 0) toast.success(`${scNum}: Enviada a ${resultData.enviados} proveedor(es)`);
      if (resultData.errores.length > 0) resultData.errores.forEach(e => toast.error(String(e)));
    } catch { }

    // PDF preview
    try {
      const pdfRes = await api.get(`/compras/cotizaciones/${scId}/pdf`, { responseType: 'blob' });
      setPdfPreviewUrl(window.URL.createObjectURL(new Blob([pdfRes.data], { type: 'application/pdf' })));
    } catch { }

    setStep(3);
    setSending(false);
    setSendingText('');
  };

  // Families summary
  const familias = useMemo(() => {
    const fam = {};
    items.forEach(it => {
      const f = it.familia || 'Sin Familia';
      if (!fam[f]) fam[f] = 0;
      fam[f]++;
    });
    return fam;
  }, [items]);

  return (
    <div className="p-4 md:p-6 max-w-5xl mx-auto" data-testid="solicitud-cotizacion-page">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 bg-[#003366] rounded-lg flex items-center justify-center">
          <FileText className="w-5 h-5 text-white" />
        </div>
        <div>
          <h1 className="text-xl font-bold text-gray-900">Solicitud de Cotizacion</h1>
          <p className="text-sm text-muted-foreground">Envie cotizaciones a multiples proveedores</p>
        </div>
      </div>

      {/* Stepper */}
      {step < 3 && (
        <div className="flex items-center gap-2 mb-6 bg-gray-50 rounded-lg p-3" data-testid="stepper">
          {STEPS.map((label, i) => (
            <div key={label} className="flex items-center gap-2">
              <div
                className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold
                  ${i === step ? 'bg-[#003366] text-white' : i < step ? 'bg-green-600 text-white' : 'bg-gray-200 text-gray-500'}`}
              >
                {i < step ? <CheckCircle className="w-4 h-4" /> : i + 1}
              </div>
              <span className={`text-sm ${i === step ? 'font-semibold text-gray-900' : 'text-gray-500'} hidden sm:inline`}>
                {label}
              </span>
              {i < STEPS.length - 1 && <ChevronRight className="w-4 h-4 text-gray-300" />}
            </div>
          ))}
        </div>
      )}

      {/* Step 0: Items */}
      {step === 0 && (
        <div className="space-y-4" data-testid="step-items">
          {/* Iter 292.b — Cargar Requisición: seleccionar folio de una Req.
              "pendiente" o "en_proceso" y pegar sus ítems en el detalle a cotizar. */}
          <div className="border rounded-lg p-4 bg-white" data-testid="cargar-requisicion-section">
            <div className="flex items-center gap-2 mb-2">
              <ClipboardList className="w-4 h-4 text-[#003366]" />
              <h3 className="text-sm font-semibold text-gray-700">Cargar desde Requisición</h3>
              <span className="text-[10px] text-gray-500">
                (Sólo autorizadas — puede agregar varias)
              </span>
            </div>
            <SearchableCombobox
              items={requisicionesDisponibles
                .filter(r => !requisicionesCargadas.some(rc => String(rc.numero) === String(r.numero)))
                .map(r => ({
                  value: String(r.numero),
                  label: `REQ-${r.numero}${r.ot_numero ? ` · OT ${r.ot_numero}` : ''}${r.solicitante ? ` · ${r.solicitante}` : ''}`,
                  subLabel: `${(r.estado || '').toUpperCase()} · ${(r.items || []).length} ítem(s)`,
                }))}
              value={reqComboValue}
              onChange={(sel) => sel?.value && cargarRequisicion(sel.value)}
              placeholder={requisicionesLoading ? 'Cargando requisiciones...' : 'Buscar por N° REQ, OT o solicitante...'}
              emptyMessage={requisicionesLoading ? 'Cargando...' : 'Sin requisiciones pendientes'}
              testid="combo-requisicion"
            />
            {requisicionesCargadas.length > 0 && (
              <div className="mt-3 space-y-1.5" data-testid="requisiciones-cargadas">
                <p className="text-[11px] font-medium text-gray-600">
                  Requisiciones incluidas ({requisicionesCargadas.length}):
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {requisicionesCargadas.map(rc => (
                    <div
                      key={rc.numero}
                      className="inline-flex items-center gap-1.5 px-2 py-1 rounded bg-indigo-50 border border-indigo-200 text-[11px] text-indigo-800"
                      data-testid={`req-cargada-${rc.numero}`}
                    >
                      <ClipboardList className="w-3 h-3" />
                      <span className="font-semibold">REQ-{rc.numero}</span>
                      {rc.ot_numero && <span className="text-indigo-600">· OT {rc.ot_numero}</span>}
                      <span className="text-indigo-500">· {rc.items_count} ítem(s)</span>
                      <button
                        type="button"
                        onClick={() => descargarRequisicion(rc.numero)}
                        className="ml-1 text-indigo-400 hover:text-red-500"
                        title={`Quitar Requisición ${rc.numero} del detalle`}
                        data-testid={`req-cargada-remove-${rc.numero}`}
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Search material from DB */}
          {!manualMode && (
            <div className="border rounded-lg p-4 bg-white">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                  <Search className="w-4 h-4" /> Buscar Material / Servicio
                </h3>
                <button onClick={() => setManualMode(true)}
                  className="text-xs text-[#003366] hover:underline" data-testid="toggle-manual">
                  No esta en la lista? Agregar manualmente
                </button>
              </div>
              <div className="relative">
                <Input value={matSearch} onChange={e => { setMatSearch(e.target.value); setShowMatResults(true); }}
                  placeholder="Buscar por descripcion, codigo o familia..."
                  className="text-sm pr-8" data-testid="mat-search-input"
                  onFocus={() => matResults.length > 0 && setShowMatResults(true)} />
                {matLoading && <Loader2 className="w-4 h-4 animate-spin absolute right-3 top-2.5 text-muted-foreground" />}
              </div>
              {/* Search results dropdown */}
              {showMatResults && matResults.length > 0 && (
                <div className="mt-1 border rounded-lg max-h-[450px] overflow-y-auto bg-white shadow-lg divide-y" data-testid="mat-results">
                  <div className="px-3 py-1.5 bg-gray-50 text-[11px] text-gray-500 sticky top-0 z-10 border-b">
                    {matResults.length} materiales encontrados
                  </div>
                  {matResults.map((mat, i) => (
                    <div key={mat.codigo || `mat-${i}`} className="px-3 py-2 hover:bg-blue-50 cursor-pointer flex items-center gap-3"
                      onClick={() => addMaterialItem(mat)} data-testid={`mat-result-${i}`}>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">
                          {mat.descripcion2 || mat.descripcion}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {mat.codigo && <span className="font-mono mr-2">{mat.codigo}</span>}
                          {mat.unidad && <span className="mr-2">{mat.unidad}</span>}
                          {mat.familia_nombre && <span className="text-blue-600">{mat.familia_nombre}</span>}
                        </p>
                      </div>
                      <Plus className="w-4 h-4 text-[#003366] shrink-0" />
                    </div>
                  ))}
                </div>
              )}
              {showMatResults && matSearch.length >= 2 && matResults.length === 0 && !matLoading && (
                <div className="mt-1 border rounded px-3 py-2 text-sm text-muted-foreground bg-gray-50">
                  Sin resultados para "{matSearch}".{' '}
                  <button onClick={() => { setManualMode(true); setManualDesc(matSearch); }}
                    className="text-[#003366] hover:underline font-medium">
                    Agregar manualmente
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Manual add mode */}
          {manualMode && (
            <div className="border rounded-lg p-4 bg-white">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                  <Plus className="w-4 h-4" /> Agregar Item Manualmente
                </h3>
                <button onClick={() => setManualMode(false)}
                  className="text-xs text-[#003366] hover:underline" data-testid="toggle-search">
                  Volver a buscar en catalogo
                </button>
              </div>
              <div className="flex gap-2 flex-wrap">
                <Input value={manualDesc} onChange={e => setManualDesc(e.target.value)}
                  placeholder="Descripcion del material/servicio" className="flex-1 min-w-[200px] text-sm"
                  data-testid="item-desc-input"
                  onKeyDown={e => e.key === 'Enter' && addManualItem()} />
                <NumberInput value={manualCant} allowNegative={false} onChange={clean => setManualCant(clean)}
                  placeholder="Cant." className="w-20 text-sm" data-testid="item-cant-input" />
                <Input value={manualUnd} onChange={e => setManualUnd(e.target.value)}
                  placeholder="Und" className="w-16 text-sm" data-testid="item-und-input" />
                <Button onClick={addManualItem} size="sm" className="bg-[#003366]" data-testid="add-item-btn">
                  <Plus className="w-4 h-4 mr-1" /> Agregar
                </Button>
              </div>
            </div>
          )}

          {/* Items list — Iter 292.q (Carlos, feb-2026): grilla estilo Excel
              con columnas Cód. Producto / Nombre / Cant. / Un. Medida / OT / Obs.
              Muestra 10 líneas y scroll vertical si hay más. */}
          {items.length > 0 && (
            <div className="border rounded-lg bg-white overflow-hidden">
              <div className="bg-gray-50 px-4 py-2 flex items-center justify-between border-b">
                <span className="text-sm font-semibold text-gray-700">
                  <Package className="w-4 h-4 inline mr-1" /> {items.length} item(s) a cotizar
                </span>
                {Object.keys(familias).length > 1 && (
                  <span className="text-xs text-muted-foreground">
                    {Object.entries(familias).map(([f, c]) => `${f}: ${c}`).join(' | ')}
                  </span>
                )}
              </div>
              {/* Iter 286.u — Aviso de items con cantidad 0/vacía */}
              {items.some(it => !Number(it.cantidad) || Number(it.cantidad) <= 0) && (
                <div className="bg-red-50 border-b border-red-200 px-4 py-1.5 text-xs text-red-700" data-testid="alerta-cantidad-vacia">
                  ⚠ Hay items con cantidad en 0 — completa la cantidad para poder continuar.
                </div>
              )}
              <div className="max-h-[440px] overflow-y-auto">
                <table className="w-full text-xs" data-testid="items-grid">
                  <thead className="bg-gray-100 sticky top-0 z-10">
                    <tr className="text-[11px] font-semibold text-gray-700 uppercase tracking-tight">
                      <th className="px-2 py-2 w-10 text-center border-b">#</th>
                      <th className="px-2 py-2 w-32 text-left border-b">Cód. Producto</th>
                      <th className="px-2 py-2 text-left border-b">Nombre</th>
                      <th className="px-2 py-2 w-24 text-center border-b">Cant.</th>
                      <th className="px-2 py-2 w-24 text-center border-b">Un. Medida</th>
                      <th className="px-2 py-2 w-16 text-center border-b">OT</th>
                      <th className="px-2 py-2 text-left border-b">Observación</th>
                      <th className="px-2 py-2 w-10 border-b"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((item, idx) => {
                      const puedeEditarProd = canEditProducto(item);
                      const cantInvalida = !item.cantidad || Number(item.cantidad) <= 0;
                      return (
                        <tr
                          key={`item-${idx}-${item.codigo || item.descripcion}`}
                          className={`border-b hover:bg-blue-50/40 ${idx % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'}`}
                          data-testid={`item-row-${idx}`}
                        >
                          <td className="px-2 py-1.5 text-center text-gray-500 font-mono align-middle">{idx + 1}</td>
                          <td className="px-2 py-1.5 text-gray-700 font-mono text-[11px] align-middle truncate" title={item.codigo || ''}>
                            {item.codigo || <span className="text-gray-300 italic">—</span>}
                          </td>
                          <td className="px-2 py-1.5 align-middle min-w-0">
                            <div className="flex items-center gap-1.5 min-w-0">
                              <span
                                className={`text-sm font-medium text-gray-900 truncate ${puedeEditarProd ? 'cursor-pointer hover:text-[#003366] hover:underline' : ''}`}
                                title={puedeEditarProd ? 'Click para cambiar el producto' : 'Solo el emisor de la Requisición, Gerencia o Administrador puede cambiar el producto'}
                                onClick={() => puedeEditarProd && updateItemDescripcion(idx)}
                                data-testid={`item-desc-${idx}`}
                              >
                                {item.descripcion || <span className="italic text-gray-400">(Sin descripción)</span>}
                              </span>
                              {puedeEditarProd ? (
                                <button
                                  type="button"
                                  onClick={() => updateItemDescripcion(idx)}
                                  className="text-gray-400 hover:text-[#003366] shrink-0"
                                  title="Cambiar producto"
                                  data-testid={`item-edit-desc-${idx}`}
                                >
                                  <Edit2 className="w-3 h-3" />
                                </button>
                              ) : (
                                <Lock
                                  className="w-3 h-3 text-gray-300 shrink-0"
                                  aria-label="Bloqueado — solo el emisor de la Requisición, Gerencia o Administrador puede cambiar el producto"
                                />
                              )}
                              {item.requisicion && (
                                <span
                                  className="shrink-0 inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-indigo-50 text-indigo-700 text-[10px] font-medium border border-indigo-200"
                                  title={`Ítem cargado desde Requisición ${item.requisicion}`}
                                  data-testid={`item-req-badge-${idx}`}
                                >
                                  <ClipboardList className="w-2.5 h-2.5" /> Req {item.requisicion}
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="px-1 py-1 align-middle">
                            <EditableInput
                              value={item.cantidad ?? ''}
                              onCommit={(v) => updateItemCantidad(idx, v)}
                              confirmMessage="¿Está seguro de cambiar la cantidad?"
                              type="number"
                              step="0.01"
                              min="0"
                              className={`w-full text-xs h-7 px-1.5 text-right ${cantInvalida ? 'border-red-400 bg-red-50' : ''}`}
                              testid={`item-cant-edit-${idx}`}
                            />
                          </td>
                          <td className="px-1 py-1 align-middle">
                            <EditableInput
                              value={item.unidad || ''}
                              onCommit={(v) => updateItemUnidad(idx, v)}
                              confirmMessage="¿Está seguro de cambiar la unidad de medida?"
                              placeholder="UN"
                              className="w-full text-xs h-7 px-1.5 text-center"
                              testid={`item-und-edit-${idx}`}
                            />
                          </td>
                          <td className="px-2 py-1.5 text-center text-gray-600 font-mono align-middle">
                            {item.ot_numero || <span className="text-gray-300">—</span>}
                          </td>
                          <td className="px-1 py-1 align-middle">
                            <Input
                              value={item.observacion || ''}
                              onChange={e => updateItemObs(idx, e.target.value)}
                              placeholder="Observación..."
                              className="w-full text-xs h-7 border-dashed"
                              data-testid={`item-obs-${idx}`}
                            />
                          </td>
                          <td className="px-1 py-1 text-center align-middle">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => removeItem(idx)}
                              className="text-red-400 hover:text-red-600 h-7 w-7 p-0"
                              data-testid={`item-delete-${idx}`}
                              title={`Eliminar ítem: ${item.descripcion || 'sin descripción'}`}
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {items.length > 10 && (
                <div className="bg-gray-50 px-4 py-1.5 border-t text-[10px] text-gray-500 text-right">
                  {items.length} filas · desplázate hacia abajo para ver todas
                </div>
              )}
            </div>
          )}

          {items.length === 0 && (
            <div className="border-2 border-dashed rounded-lg p-8 text-center text-muted-foreground">
              <Package className="w-8 h-8 mx-auto mb-2 opacity-50" />
              <p className="text-sm">No hay items. Agregue items manualmente o inicie desde Pendientes de Compra.</p>
            </div>
          )}
        </div>
      )}

      {/* Step 1: Providers */}
      {step === 1 && (
        <div className="space-y-4" data-testid="step-proveedores">
          {/* Search */}
          <div className="border rounded-lg p-4 bg-white">
            <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
              <Search className="w-4 h-4" /> Buscar Proveedores (Alegra)
            </h3>
            <div className="relative">
              <Input value={provSearch} onChange={e => setProvSearch(e.target.value)}
                placeholder="Buscar por nombre, RUT o email..." className="text-sm pr-8"
                data-testid="prov-search-input" />
              {searchingProv && <Loader2 className="w-4 h-4 animate-spin absolute right-3 top-2.5 text-muted-foreground" />}
            </div>

            {/* Results */}
            {provResults.length > 0 && (
              <div className="mt-2 border rounded max-h-[200px] overflow-y-auto divide-y">
                {provResults.map((p, pIdx) => {
                  const alreadyAdded = proveedores.some(pp => pp.nombre === p.nombre && pp.rut === p.rut);
                  return (
                    <div key={p.rut || p.nombre} className="px-3 py-2 flex items-center justify-between hover:bg-gray-50 text-sm"
                      data-testid={`prov-result-${pIdx}`}>
                      <div className="min-w-0 flex-1">
                        <p className="font-medium text-gray-900 truncate">{p.nombre}</p>
                        <p className="text-xs text-muted-foreground">
                          {p.rut} {p.email ? `| ${p.email}` : '(sin email)'}
                          {p.ciudad ? ` | ${p.ciudad}` : ''}
                        </p>
                      </div>
                      <Button size="sm" variant={alreadyAdded ? "outline" : "default"}
                        disabled={alreadyAdded}
                        onClick={() => addProveedor(p)}
                        className={alreadyAdded ? '' : 'bg-[#003366] text-xs'}
                        data-testid={`add-prov-${pIdx}`}>
                        {alreadyAdded ? <CheckCircle className="w-3.5 h-3.5 text-green-600" /> : <Plus className="w-3.5 h-3.5" />}
                      </Button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Selected providers */}
          {proveedores.length > 0 && (
            <div className="border rounded-lg bg-white overflow-hidden">
              <div className="bg-gray-50 px-4 py-2 border-b">
                <span className="text-sm font-semibold text-gray-700">
                  <Users className="w-4 h-4 inline mr-1" /> {proveedores.length} proveedor(es) seleccionado(s)
                </span>
              </div>
              <div className="divide-y">
                {proveedores.map((prov, idx) => (
                  <div key={prov.rut || `prov-${idx}`} className="px-4 py-2.5 flex items-center gap-3" data-testid={`selected-prov-${idx}`}>
                    <Building className="w-4 h-4 text-[#003366] shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900">{prov.nombre}</p>
                      <p className="text-xs text-muted-foreground">{prov.rut}</p>
                    </div>
                    <Input value={prov.email} onChange={e => updateProvEmail(idx, e.target.value)}
                      placeholder="email@proveedor.com" className="w-56 text-sm"
                      data-testid={`prov-email-${idx}`} />
                    <Button variant="ghost" size="sm" onClick={() => removeProveedor(idx)}
                      className="text-red-400 hover:text-red-600">
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {proveedores.length === 0 && (
            <div className="border-2 border-dashed rounded-lg p-8 text-center text-muted-foreground">
              <Users className="w-8 h-8 mx-auto mb-2 opacity-50" />
              <p className="text-sm">Busque y seleccione proveedores para enviar la cotizacion.</p>
            </div>
          )}
        </div>
      )}

      {/* Step 2: Review */}
      {step === 2 && (
        <div className="space-y-4" data-testid="step-review">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Summary */}
            <div className="border rounded-lg p-4 bg-white space-y-3">
              <h3 className="text-sm font-semibold text-gray-700">Resumen</h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Items a cotizar:</span>
                  <span className="font-semibold">{items.length}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Proveedores:</span>
                  <span className="font-semibold">{proveedores.length}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-muted-foreground">Dias para responder:</span>
                  <Input value={diasVenc} onChange={e => setDiasVenc(parseInt(e.target.value) || 5)}
                    type="number" className="w-16 h-7 text-sm text-center" data-testid="dias-venc" />
                </div>
              </div>
              {/* Iter 286.n — OT a nivel solicitud
                  Iter 292.m: SearchableCombobox en lugar de <select>.
                  Iter 292.o: bloquear cuando hay múltiples OTs en los ítems
                  (el PDF hará Referencia Interna al pie con el listado). */}
              <div>
                <label className="text-xs font-medium text-gray-600 mb-1 block">OT</label>
                <SearchableCombobox
                  items={[
                    { value: 'por_definir', label: 'OT: Por definir', subLabel: 'Aún no definida' },
                    ...otsActivas.map((ot) => ({
                      value: String(ot.numero_ot),
                      label: `OT ${ot.numero_ot}`,
                      subLabel: [ot.cliente_nombre, ot.descripcion].filter(Boolean).join(' · '),
                    })),
                  ]}
                  value={otSolicitud}
                  onChange={(sel) => setOtSolicitud(sel ? sel.value : '')}
                  placeholder={otsLoading ? 'Cargando OTs...' : 'Buscar OT por N° o cliente...'}
                  emptyMessage={otsLoading ? 'Cargando...' : 'Sin OTs vigentes'}
                  testid="select-ot-solicitud"
                  disabled={otsBloqueadaMulti}
                />
                {otsBloqueadaMulti ? (
                  <p className="text-[10px] text-amber-700 mt-0.5" data-testid="ots-multi-notice">
                    ⚠ El detalle contiene {uniqueOtsItems.length} OTs distintas ({uniqueOtsItems.join(', ')}).
                    Se listarán como <strong>Referencia Interna</strong> al pie del PDF; el encabezado no muestra una OT única.
                  </p>
                ) : uniqueOtsItems.length === 1 ? (
                  <p className="text-[10px] text-green-700 mt-0.5">
                    OT auto-detectada desde el detalle. Aparecerá en el encabezado del PDF.
                  </p>
                ) : (
                  <p className="text-[10px] text-muted-foreground mt-0.5">
                    OT vigente asociada a esta solicitud. Si aún no está definida, elige "OT: Por definir".
                  </p>
                )}
              </div>
              <div>
                <label className="text-xs font-medium text-gray-600 mb-1 block">Mensaje adicional (opcional)</label>
                <textarea value={mensajeAdicional} onChange={e => setMensajeAdicional(e.target.value)}
                  placeholder="Ej: Favor considerar despacho a Puerto Montt..."
                  className="w-full h-20 px-3 py-2 rounded-md border text-sm resize-none" data-testid="mensaje-adicional" />
              </div>
            </div>

            {/* Providers list */}
            <div className="border rounded-lg p-4 bg-white">
              <h3 className="text-sm font-semibold text-gray-700 mb-2">Se enviara a:</h3>
              <div className="space-y-2 max-h-[200px] overflow-y-auto">
                {proveedores.map((p) => (
                  <div key={p.rut || p.nombre} className="flex items-center gap-2 text-sm bg-gray-50 rounded px-3 py-1.5">
                    <Building className="w-3.5 h-3.5 text-[#003366]" />
                    <span className="font-medium truncate flex-1">{p.nombre}</span>
                    <span className="text-xs text-muted-foreground">{p.email}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Items preview */}
          <div className="border rounded-lg bg-white overflow-hidden">
            <div className="bg-gray-50 px-4 py-2 border-b">
              <span className="text-sm font-semibold text-gray-700">Items a Cotizar</span>
            </div>
            <table className="w-full text-sm">
              <thead className="bg-[#003366] text-white text-xs">
                <tr>
                  <th className="px-3 py-1.5 text-left w-8">#</th>
                  <th className="px-3 py-1.5 text-left">Descripcion</th>
                  <th className="px-3 py-1.5 text-center w-16">Cant.</th>
                  <th className="px-3 py-1.5 text-center w-12">Und</th>
                  <th className="px-3 py-1.5 text-left">Observacion</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {items.map((it, i) => (
                  <tr key={it.codigo || `sum-${i}`} className={i % 2 === 0 ? 'bg-gray-50' : ''}>
                    <td className="px-3 py-1.5 text-xs text-muted-foreground">{i + 1}</td>
                    <td className="px-3 py-1.5">
                      {it.descripcion}
                      {it.ot_numero && <span className="text-xs text-muted-foreground ml-1">(OT: {it.ot_numero})</span>}
                    </td>
                    <td className="px-3 py-1.5 text-center">{it.cantidad}</td>
                    <td className="px-3 py-1.5 text-center">{it.unidad}</td>
                    <td className="px-3 py-1.5 text-xs text-muted-foreground italic">{it.observacion || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
            <p className="text-xs text-blue-700">
              Se enviara un correo desde <strong>compras@chirinosteel.com</strong> con el PDF adjunto a cada proveedor seleccionado.
              El proveedor debera responder al mismo correo con su cotizacion.
            </p>
          </div>

          {sendError && (
            <div className="bg-red-50 border border-red-300 rounded-lg p-3" data-testid="send-error">
              <p className="text-sm text-red-700 font-medium">Error al procesar:</p>
              <p className="text-xs text-red-600 mt-1 break-all">{sendError}</p>
              <p className="text-xs text-gray-500 mt-2">Si el problema persiste, intente recargar la pagina (F5) y volver a crear la solicitud.</p>
            </div>
          )}
        </div>
      )}

      {/* Step 3: Success */}
      {step === 3 && (
        <div className="text-center py-8 space-y-4" data-testid="step-success">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto">
            <CheckCircle className="w-8 h-8 text-green-600" />
          </div>
          <h2 className="text-lg font-bold text-gray-900">Solicitud Enviada Exitosamente</h2>
          {sendResult && (
            <p className="text-sm text-muted-foreground">
              Correos enviados: <strong className="text-green-600">{sendResult.enviados}</strong> de {sendResult.total} proveedor(es)
              {sendResult.errores.length > 0 && (
                <span className="text-red-500 block mt-1">
                  {sendResult.errores.length} error(es) de envio
                </span>
              )}
            </p>
          )}
          {!sendResult && (
            <p className="text-sm text-muted-foreground">
              Las solicitudes de cotizacion fueron enviadas a {proveedores.length} proveedor(es).
              <br/>Podra hacer seguimiento en el listado de cotizaciones.
            </p>
          )}
          {pdfPreviewUrl && (
            <div className="max-w-2xl mx-auto border rounded-lg overflow-hidden mt-4">
              <div className="flex items-center gap-2 px-3 py-2 bg-gray-100 border-b">
                <FileSearch className="w-4 h-4 text-blue-600" />
                <span className="text-xs font-semibold text-gray-600">PDF Enviado</span>
              </div>
              <iframe src={pdfPreviewUrl} className="w-full h-[500px] border-0" title="PDF Solicitud" />
            </div>
          )}
          <div className="flex gap-3 justify-center mt-4">
            <Button onClick={() => navigate('/compras/cotizaciones')} className="bg-[#003366] gap-1.5"
              data-testid="ver-listado-btn">
              <FileText className="w-4 h-4" /> Ver Solicitudes de Cotizacion
            </Button>
            <Button variant="outline" onClick={() => {
              setStep(0); setItems([]); setProveedores([]); setCreatedId(null);
              setPdfPreviewUrl(null); setMensajeAdicional(''); setSendResult(null);
            }} data-testid="nueva-solicitud-btn">
              <Plus className="w-4 h-4 mr-1" /> Nueva Solicitud
            </Button>
          </div>
        </div>
      )}

      {/* Navigation buttons */}
      {step < 3 && (
        <div className="flex justify-between mt-6 pt-4 border-t">
          <Button variant="outline" onClick={step === 0 ? () => navigate(-1) : prevStep} data-testid="btn-prev">
            <ChevronLeft className="w-4 h-4 mr-1" /> {step === 0 ? 'Cancelar' : 'Anterior'}
          </Button>
          {step < 2 ? (
            <Button onClick={nextStep} disabled={!canGoNext} className="bg-[#003366] gap-1"
              data-testid="btn-next">
              Siguiente <ChevronRight className="w-4 h-4" />
            </Button>
          ) : (
            <Button onClick={handleCrearYEnviar} disabled={sending} className="bg-emerald-600 hover:bg-emerald-700 gap-1.5"
              data-testid="btn-enviar">
              {sending ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  {sendingText || 'Procesando...'}
                </>
              ) : (
                <>
                  <Send className="w-4 h-4" />
                  Crear y Enviar Cotizacion
                </>
              )}
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
